import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AccordionItem,
  Typography,
  TextField,
  Button,
  Spinner,
  Collapsible,
  Drawer,
} from '@genie-ui/react';
import './assign-team.scss';
import CYBER_CONSTANT from 'constants/CyberConstants';
import TeamManagementList from 'components/molecules/teammanagementlist';
import TeamManagementMenu from 'components/molecules/teammanagementmenu';
import {
  addTeamMember, fetchTeamMembers, removeTeamMember, updateRole,
} from 'components/pages/cyber/api';
import { getStringWithBoldFormatting, isBusinessEmail, isValidEmail } from 'utils/Utilities';
import SnackbarComponent from 'components/atoms/snackbarcomponent';
import CloseIcon from '@mui/icons-material/Close';
import Constants from 'constants/Constants';
import { useSelector } from 'react-redux';
import TeamRolesTable from 'components/atoms/teamrolestable';

function AssignTeam({
  open = true,
  onClose,
  actisDataObj,
  dashboardId = '',
}) {
  const {
    TEAM_MANAGEMENT,
    TEAM_MANAGEMENT: {
      INVITATION_REQUIRED,
      ERROR_MESSAGES,
      RESPONSES,
    },
  } = CYBER_CONSTANT;

  const { user: { primaryEmailDec = '' } = {} } = useSelector((state) => state.auth);

  const [role, setRole] = useState(TEAM_MANAGEMENT.ROLES.CONTRIBUTOR);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [inviteNote, setInviteNote] = useState('');

  const [successSnackbar, setSuccessSnackbar] = useState('');
  const [failureSnackbar, setFailureSnackbar] = useState('');

  const [showInvitationVerification, setShowInvitationVerfication] = useState(false);

  const [teamMembersList, setTeamMembersList] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false);

  const fetchTeamMembersData = () => {
    setTeamMembersLoading(true);
    fetchTeamMembers(dashboardId)?.then((response) => {
      if (response?.body?.systemMessage?.success) {
        const { data } = response?.body || {};
        setTeamMembersList(data);
      }
    }).finally(() => {
      setTeamMembersLoading(false);
    });
  };

  useEffect(() => {
    if (open) {
      fetchTeamMembersData();
    }
    return () => {
      setEmail('');
      setError('');
    };
  }, [open]);

  const addMember = () => {
    const payload = {
      email,
      type: role,
      inviteNote,
    };
    setAddMemberLoading(true);
    addTeamMember(payload, dashboardId)?.then((response) => {
      if (response?.body?.systemMessage?.success) {
        setSuccessSnackbar(RESPONSES.ADD_INVITE_SUCCESS);
        fetchTeamMembersData();
      } else {
        setFailureSnackbar(RESPONSES.ADD_INVITE_ERROR);
      }
    }).finally(() => {
      setAddMemberLoading(false);
      setEmail('');
      setInviteNote('');
    });

    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.SENDS_INVITE,
        activityMeta: `${CYBER_CONSTANT.ACTIS.SENDS_INVITE.activityMeta} ${role}`,
        activityCategory: role,
        activityValue: email,
        ...actisDataObj,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const onRemoveTeamMember = (emailToBeRemoved, roleToBeRemoved) => {
    setRemoveMemberLoading(true);
    removeTeamMember(emailToBeRemoved, dashboardId)?.then((response) => {
      if (response?.body?.systemMessage?.success) {
        setSuccessSnackbar(RESPONSES.REMOVE_INVITE_SUCCESS);
        fetchTeamMembersData();
      } else {
        setFailureSnackbar(RESPONSES.REMOVE_INVITE_ERROR);
      }
    }).finally(() => {
      setRemoveMemberLoading(false);
    });
    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.REMOVES_MEMBER,
        activityMeta: `${CYBER_CONSTANT.ACTIS.REMOVES_MEMBER.activityMeta} ${role}`,
        activityCategory: roleToBeRemoved,
        activityValue: emailToBeRemoved,
        ...actisDataObj,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const onUpdateRole = ({ email: newEmail, role: newRole }) => {
    setRemoveMemberLoading(true);
    updateRole({
      email: newEmail,
      type: newRole,
      dashboardId,
    })?.then((response) => {
      if (response?.body?.systemMessage?.success) {
        setSuccessSnackbar(RESPONSES.UPDATE_ROLE_SUCCESS);
        fetchTeamMembersData();
      } else {
        setFailureSnackbar(RESPONSES.UPDATE_ROLE_ERROR);
      }
    }).finally(() => {
      setRemoveMemberLoading(false);
    });
    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.UPDATE_ROLE,
        activityMeta: `${CYBER_CONSTANT.ACTIS.UPDATE_ROLE.activityMeta} ${newRole}`,
        activityCategory: newRole,
        activityValue: newEmail,
        ...actisDataObj,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const validateAddMember = () => {
    const inputDomain = email?.split('@')?.[1] || '';
    const domain = primaryEmailDec?.split('@')?.[1] || '';
    if (inputDomain?.toLowerCase() !== domain?.toLowerCase()) {
      setShowInvitationVerfication(true);
      return;
    }
    addMember();
  };

  const onVerificationCancel = () => {
    setEmail('');
    setShowInvitationVerfication(false);
  };

  const onVerificationProceed = () => {
    addMember();
    setShowInvitationVerfication(false);
  };

  const closeSuccessSnackbar = () => {
    setSuccessSnackbar('');
  };

  const closeFailureSnackbar = () => {
    setFailureSnackbar('');
  };

  const onEmailChange = (event) => {
    const value = event?.target?.value || '';
    setEmail(value);
    if (teamMembersList?.some((item) => (
      item?.email?.toLowerCase() === value?.toLowerCase()
    ))) {
      setError(ERROR_MESSAGES.ALREADY_INVITED_USER);
    } else if (isValidEmail(value) && !isBusinessEmail(value)) {
      setError(ERROR_MESSAGES.NON_BUSINESS_EMAIL);
    } else {
      setError('');
    }
  };

  const onInviteNoteChange = (event) => {
    const value = event?.target?.value || '';
    setInviteNote(value);
  };

  const onCloseDrawer = () => {
    onClose(teamMembersList?.length);
    setRole(TEAM_MANAGEMENT.ROLES.CONTRIBUTOR);
  };

  return (
    <Drawer
      open={open}
      className="assignTeamDialog"
      anchor="right"
      closeOnBackdropClick={false}
    >
      <div>
        <div className="gaplite-c-assignTeam__titleDiv">
          <Typography className="gaplite-c-assignTeam__title gaplite-c-assignTeam__drawertitle">
            {TEAM_MANAGEMENT.TITLE}
          </Typography>
          <CloseIcon
            onClick={onCloseDrawer}
            sx={{
              cursor: 'pointer',
            }}
          />
        </div>
        <Typography className="gaplite-c-assignTeam__subTitle">
          {TEAM_MANAGEMENT.TEAM_MANAGEMENT_DRAWER_SUBTITLE}
        </Typography>
        <AccordionItem
          key="Accordion1"
          title={TEAM_MANAGEMENT.LEARN_ABOUT_USER_ROLE}
          classContent="gaplite-c-assignTeam__accordionSection"
          classButton="gaplite-c-assignTeam__accordionButton"
        >
          <Typography
            variant="body2"
            className="gaplite-c-assignTeam__accordionSection__title"
          >
            {getStringWithBoldFormatting(TEAM_MANAGEMENT.USER_ROLE_DESCRIPTION_1)}
            <br />
            {TEAM_MANAGEMENT.USER_ROLE_DESCRIPTION_2}
          </Typography>
          <Typography
            className="gaplite-c-assignTeam__accordionSection__tableTitle"
          >
            {TEAM_MANAGEMENT.ROLES_TABLE_TITLE}
          </Typography>
          <TeamRolesTable />
        </AccordionItem>
        <div className="gaplite-c-assignTeam__formDiv">
          <div className="gaplite-c-assignTeam__titleField">
            <Typography className="gaplite-c-assignTeam__title">
              {TEAM_MANAGEMENT.EMAIL}
            </Typography>
            <div
              className={`gaplite-c-assignTeam__inputContainer ${error?.length
                > 0 && 'gaplite-c-assignTeam__error'}`}
            >
              <TextField
                className="gaplite-c-assignTeam__inputContainer__emailInput gaplite-c-assignTeam__field"
                placeholder={TEAM_MANAGEMENT.EMAIL_PLACEHOLDER}
                value={email}
                onChange={onEmailChange}
                onBlur={onEmailChange}
                onKeyDown={onEmailChange}
                disabled={showInvitationVerification}
              />
            </div>
            {error?.length > 0 ? (
              <Typography className="gaplite-c-assignTeam__inputContainer__errorMessage">{error}</Typography>
            )
              : (
                <Typography className="gaplite-c-assignTeam__helperMessage">
                  {TEAM_MANAGEMENT.EMAIL_ERROR_MESSAGE}
                </Typography>
              )}
          </div>
          <div className="gaplite-c-assignTeam__titleField gaplite-c-assignTeam__roleField">
            <Typography className="gaplite-c-assignTeam__title">
              {TEAM_MANAGEMENT.ROLE}
            </Typography>
            <TeamManagementMenu
              options={['Manager', 'Contributor']}
              className="gaplite-c-assignTeam__inputContainer__menu"
              role={role}
              setRole={setRole}
              disabled={showInvitationVerification}
            />
          </div>
        </div>

        <div className="gaplite-c-assignTeam__inviteDiv">
          <Typography className="gaplite-c-assignTeam__title">
            {TEAM_MANAGEMENT.ADD_INVITE_NOTES}
            <i className="gaplite-c-assignTeam__italic">
              {TEAM_MANAGEMENT.OPTIONAL}
            </i>
          </Typography>
          <TextField
            multiline
            maxRows={4}
            minRows={2}
            fullWidth
            onChange={onInviteNoteChange}
            onBlur={onInviteNoteChange}
            onKeyDown={onInviteNoteChange}
            value={inviteNote}
            placeholder={TEAM_MANAGEMENT.INVITE_PLACEHOLDER}
            maxCharacterCount={5000}
          />
        </div>

        <div className="gaplite-c-assignTeam__buttonContainer">
          {addMemberLoading ? <Spinner /> : (
            <Button
              className="gaplite-c-assignTeam__invite"
              onClick={validateAddMember}
              disabled={(
                !isValidEmail(email)
                || !!error.length
                || showInvitationVerification
              )}
            >
              {TEAM_MANAGEMENT.ADD}
            </Button>
          )}
        </div>

        <Collapsible
          in={showInvitationVerification}
          mountOnEnter
          unmountOnExit
          timeout={500}
        >
          <div className="gaplite-c-assignTeam__invitationRequired">
            <div className="gaplite-c-assignTeam__invitationRequired__header">
              {INVITATION_REQUIRED.HEADER}
            </div>
            <div className="gaplite-c-assignTeam__invitationRequired__body">
              {INVITATION_REQUIRED.BODY
                ?.replace(INVITATION_REQUIRED?.INVITEE_EMAIL, primaryEmailDec.split('@')[1])
                ?.replace(INVITATION_REQUIRED?.EMAIL, email?.split('@')?.[1] || '')}
              <div className="gaplite-c-assignTeam__invitationRequired__actionButtonContainer">
                <Button
                  variant="primary"
                  onClick={onVerificationProceed}
                >
                  {INVITATION_REQUIRED?.VERIFY_AND_SEND}
                </Button>
                <Button
                  variant="secondary"
                  onClick={onVerificationCancel}
                >
                  {INVITATION_REQUIRED?.CANCEL}
                </Button>
              </div>
            </div>
          </div>
        </Collapsible>
        <div className="gaplite-c-assignTeam__data">
          <TeamManagementList
            teamMembersList={teamMembersList}
            loading={teamMembersLoading}
            fetchTeamMembersData={fetchTeamMembersData}
            onRemoveTeamMember={onRemoveTeamMember}
            onUpdateRole={onUpdateRole}
            removeMemberLoading={removeMemberLoading}
            showInvitationVerification={showInvitationVerification}
            dashboardId={dashboardId}
          />
        </div>
        <SnackbarComponent
          msg={failureSnackbar}
          onClose={closeFailureSnackbar}
          variant={Constants.SNACKBAR.ERROR}
          open={failureSnackbar.length}
          anchorOrigin={{
            vertical: 'bottom', horizontal: 'center',
          }}
        />
        <SnackbarComponent
          msg={successSnackbar}
          onClose={closeSuccessSnackbar}
          variant={Constants.SNACKBAR.SUCCESS}
          open={successSnackbar?.length}
          anchorOrigin={{
            vertical: 'bottom', horizontal: 'center',
          }}
        />
      </div>
    </Drawer>
  );
}

AssignTeam.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  actisDataObj: PropTypes.instanceOf(Object).isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default AssignTeam;
